import type { SunrunShopProductPageCF } from '@/types';
import { SellingPlanInterval } from '@/types/graphql/storefront';
import { formatUSD } from '@/utils/currency';
import type {
  CartItemInput,
  ProductVariant,
} from '../../../clients/storefront/storefront.types';

export type SellingPlanAllocation = NonNullable<
  ProductVariant['sellingPlanAllocations']
>['nodes'][number];

export type SubscriptionProductType =
  SunrunShopProductPageCF['fields']['subscriptionProductType'];

export const getFirstAvailableVariantId = (
  variants: ProductVariant[] | undefined
): string | undefined => {
  const firstAvailableVariant = variants?.find(
    (variant) => variant.quantityAvailable
  );
  return firstAvailableVariant?.id;
};

export const getFirstSellingPlanId = (variant: ProductVariant | undefined) =>
  variant?.sellingPlanAllocations?.nodes[0]?.sellingPlan.id;

export const getSelectedSellingPlanAllocation = (
  variant: ProductVariant | undefined,
  sellingPlanId: string | undefined
) =>
  variant?.sellingPlanAllocations?.nodes.find(
    ({ sellingPlan }) => sellingPlan.id === sellingPlanId
  );

export const getQueryParamValue = (value: string | string[] | undefined) =>
  typeof value === 'string' ? value : value?.[0];

const getSubscriptionType = (
  interval: SellingPlanInterval | null | undefined
) => {
  switch (interval) {
    case SellingPlanInterval.Day:
      return 'daily';
    case SellingPlanInterval.Month:
      return 'monthly';
    case SellingPlanInterval.Week:
      return 'weekly';
    case SellingPlanInterval.Year:
      return 'yearly';
    default:
      return undefined;
  }
};

const getSubscriptionFrequencyLabel = (
  interval: SellingPlanInterval | null | undefined
) => {
  switch (interval) {
    case SellingPlanInterval.Day:
      return 'day';
    case SellingPlanInterval.Month:
      return 'mo';
    case SellingPlanInterval.Week:
      return 'week';
    case SellingPlanInterval.Year:
      return 'yr';
    default:
      return undefined;
  }
};

export const formatSubscriptionPriceWithFrequency = (
  allocation: SellingPlanAllocation
) => {
  const frequency = getSubscriptionFrequencyLabel(
    allocation.sellingPlan.deliveryPolicy?.interval
  );
  const formattedAmount = formatUSD(
    allocation.checkoutChargeAmount.amount as string,
    2
  );

  if (!frequency) {
    return formattedAmount;
  }

  return `${formattedAmount}/${frequency}`;
};

export const getSellingPlanAllocations = (
  variant: ProductVariant | undefined
) => variant?.sellingPlanAllocations?.nodes ?? [];

export const isMultiSellingPlan = (
  requiresSellingPlan: boolean | null | undefined,
  variant: ProductVariant | undefined
) =>
  Boolean(requiresSellingPlan) && getSellingPlanAllocations(variant).length > 1;

export const formatDisplayPrice = ({
  totalPriceAmount,
  selectedVariant,
  isSubscriptionProduct,
}: {
  totalPriceAmount: string | undefined;
  selectedVariant: ProductVariant | undefined;
  isSubscriptionProduct: boolean;
}) => {
  const sellingPlans = getSellingPlanAllocations(selectedVariant);

  if (isSubscriptionProduct && sellingPlans.length === 1) {
    return formatSubscriptionPriceWithFrequency(sellingPlans[0]);
  }

  if (!totalPriceAmount) {
    return undefined;
  }

  return formatUSD(totalPriceAmount, 2);
};

export const formatSubscriptionLabel = (allocation: SellingPlanAllocation) => {
  const subscriptionType = getSubscriptionType(
    allocation.sellingPlan.deliveryPolicy?.interval
  );
  const formattedAmount = formatUSD(
    allocation.checkoutChargeAmount.amount as string,
    2
  );

  if (!subscriptionType) {
    return `${allocation.sellingPlan.name}`;
  }

  return `${subscriptionType[0].toUpperCase()}${subscriptionType.slice(1)} - ${formattedAmount}`;
};

export const getSubscriptionAttributes = (
  serviceContractNumber: string | undefined,
  accountId: string | undefined,
  selectedSellingPlan: SellingPlanAllocation | undefined,
  subscriptionProductType: SubscriptionProductType | undefined,
  isFacilityStateInShineAllowlist?: string
) => {
  const monitoringSubscriptionType = getSubscriptionType(
    selectedSellingPlan?.sellingPlan.deliveryPolicy?.interval
  );

  if (
    !serviceContractNumber ||
    !accountId ||
    !monitoringSubscriptionType ||
    !subscriptionProductType
  ) {
    return undefined;
  }

  return [
    {
      key: 'serviceContractNumber',
      value: serviceContractNumber,
    },
    {
      key: 'accountId',
      value: accountId,
    },
    {
      key: 'purchaseType',
      value: 'subscription',
    },
    {
      key: 'monitoringSubscriptionType',
      value: monitoringSubscriptionType,
    },
    {
      key: 'subscriptionProductType',
      value: subscriptionProductType,
    },
    ...(isFacilityStateInShineAllowlist
      ? [
          {
            key: 'isFacilityStateInShineAllowlist',
            value: isFacilityStateInShineAllowlist,
          },
        ]
      : []),
  ];
};

export const getSelectedPurchaseData = ({
  variants,
  selectedVariantId,
  selectedSellingPlanId,
  requiresSellingPlan,
  serviceContractNumber,
  accountId,
  subscriptionProductType,
  isFacilityStateInShineAllowlist,
}: {
  variants: ProductVariant[] | undefined;
  selectedVariantId: string | undefined;
  selectedSellingPlanId: string | undefined;
  requiresSellingPlan: boolean | null | undefined;
  serviceContractNumber?: string;
  accountId?: string;
  subscriptionProductType?: SubscriptionProductType;
  isFacilityStateInShineAllowlist?: string;
}) => {
  const selectedVariant = variants?.find(
    (variant) => variant.id === selectedVariantId
  );
  const isSubscriptionProduct = Boolean(requiresSellingPlan);
  const selectedSellingPlan = getSelectedSellingPlanAllocation(
    selectedVariant,
    selectedSellingPlanId
  );
  const subscriptionAttributes = getSubscriptionAttributes(
    serviceContractNumber,
    accountId,
    selectedSellingPlan,
    subscriptionProductType,
    isFacilityStateInShineAllowlist
  );
  const canPurchaseSubscription =
    !isSubscriptionProduct || Boolean(subscriptionAttributes);

  let selectedPurchase: Omit<CartItemInput, 'quantity'> | undefined;

  if (selectedVariantId && canPurchaseSubscription) {
    if (isSubscriptionProduct && selectedSellingPlanId) {
      selectedPurchase = {
        merchandiseId: selectedVariantId,
        sellingPlanId: selectedSellingPlanId,
        attributes: subscriptionAttributes,
      };
    } else {
      selectedPurchase = { merchandiseId: selectedVariantId };
    }
  }

  const totalPriceAmount = (selectedSellingPlan?.checkoutChargeAmount.amount ??
    selectedVariant?.price.amount) as string | undefined;

  return {
    selectedVariant,
    selectedSellingPlan,
    selectedPurchase,
    totalPriceAmount,
    canPurchaseSubscription,
  };
};

export const moveElementToFront = (items: string[], itemToMove: string) => {
  if (!items.includes(itemToMove)) {
    return items;
  }

  return [itemToMove, ...items.filter((item) => item !== itemToMove)];
};
